import { initI18n, loadResources } from '@tecma/i18n';
import { logger } from '@tecma/logs-fe';
import Cookies from 'js-cookie';
import { all, call, put, select } from 'redux-saga/effects';

import Project from 'client/api/Project';
import translation from 'locales/en-GB/translation.json';
import { authActions } from 'store/sagas/application/auth/auth.actions';
import { getTokenSelector, getUserSelector } from 'store/slices/auth/selectors';
import { environmentSelectors } from 'store/slices/environment/selector';
import { EnvironmentState } from 'store/slices/environment/slice';
import { loadScssFromDB } from 'theme/utils';
import type { ProjectInfo } from 'types/project';

import { HOSTNAME_QUERY_PARAM_KEY, PROJECT_ID_COOKIE_KEY, PROJECT_ID_PARAM_KEY } from '../../../../constants';
import { applicationStateActions } from '../../../slices/applicationState/slice';
import { Statuses } from 'types/quote';
import { getStatuses } from 'store/sagas/configurations/configurations.api';

const getProjectInfo = async () => {
  try {
    return Project.getProjectInfo().then((response: Response) => {
      if (response.ok) {
        return response.json();
      }
      return undefined;
    });
  } catch (err) {
    logger.error(err);
    return undefined;
  }
};

const getProjectInfoByProjectId = async (projectId: string) => {
  try {
    return Project.getProjectInfoById(projectId).then((response: Response) => {
      if (response.ok) {
        return response.json();
      }
      return undefined;
    });
  } catch (err) {
    logger.error(err);
    return undefined;
  }
};

function* getProjectInfoSaga() {
  const urlParams = new URLSearchParams(window.location.search);
  const hostnameParam = urlParams.get(HOSTNAME_QUERY_PARAM_KEY);
  const projectIdParam = urlParams.get(PROJECT_ID_PARAM_KEY);
  const projectId = Cookies.get(PROJECT_ID_COOKIE_KEY) ?? projectIdParam;
  if (!hostnameParam && projectId) {
    const { data } = yield getProjectInfoByProjectId(projectId);
    return data;
  }
  const res: { [key: string]: ProjectInfo } = yield getProjectInfo();
  const data = res.data;
  Cookies.set(PROJECT_ID_COOKIE_KEY, data.id);
  return data;
}

function* initTecmaI18NSaga() {
  const environment: EnvironmentState = yield select(environmentSelectors.environment);
  yield initI18n(
    translation,
    {
      bucketBase: `${environment.REACT_APP_BUCKET_BASEURL}`,
      productName: environment.REACT_APP_NAME!,
      apiBase: environment.REACT_APP_API_URI || '',
      debug: false,
    },
    'en-GB',
  );
}

function* loadI18NResources(projectInfo: ProjectInfo) {
  yield loadResources({
    id: projectInfo.id,
    displayName: projectInfo.displayName,
    languages: projectInfo.languages,
    fetchGlobalTranslations: true,
  });
}

function* loadStyle(projectInfo: ProjectInfo) {
  yield loadScssFromDB(projectInfo.style);
}

const toCamelCase = (str: string) => {
  return str.toLowerCase().replace(/[_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
};

const camelCaseKeys = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(camelCaseKeys);
  } else if (obj && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [toCamelCase(k), camelCaseKeys(v)]));
  }
  return obj;
};

function* getQuoteStatuses(projectId: string) {
  const response: Response = yield getStatuses(projectId);
  if (response.ok) {
    const data: Statuses = yield response.json();
    return camelCaseKeys(data);
  }
  return {};
}

export function* applicationInitSaga() {
  const resultsSaga: [void, void, ProjectInfo] = yield all([
    put(applicationStateActions.loadingRequired()),
    call(initTecmaI18NSaga),
    call(getProjectInfoSaga),
  ]);
  const projectInfo: ProjectInfo = resultsSaga[2];
  const token: IToken = yield select(getTokenSelector);
  const user: IUser = yield select(getUserSelector);
  const statuses: Statuses = yield getQuoteStatuses(projectInfo.id);
  yield put(applicationStateActions.applicationProjectInfoLoaded(projectInfo));
  yield all([
    call(loadI18NResources, projectInfo),
    call(loadStyle, projectInfo),
    put(authActions.initAuth({ projectId: projectInfo.id, user, token })),
    put(applicationStateActions.setStatuses(statuses)),
  ]);
}
